import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { setupStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignIn, ShieldStar, GoogleLogo } from "@phosphor-icons/react";
import { Logo } from "@/components/common/Logo";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, setup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null=checking, "setup", "login"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState("");

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    setupStatus()
      .then((r) => setMode(r.needs_setup ? "setup" : "login"))
      .catch(() => setMode("login"));
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password";
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await setup(name.trim(), email.trim(), password);
      toast.success("Owner account created — welcome to Vozelto");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create owner account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-[#6B3DFF]/10 blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <Logo variant="horizontal" height={40} />
        </div>

        {mode === null ? (
          <div className="card-matte p-8 text-center text-[#9a9a9a] ai-thinking" data-testid="auth-checking">Loading…</div>
        ) : mode === "setup" ? (
          <div className="card-matte p-8" data-testid="setup-card">
            <div className="flex items-center gap-2 mb-1"><ShieldStar size={20} className="brand-text" weight="fill" />
              <h1 className="font-head text-xl font-semibold">Create your Owner account</h1></div>
            <p className="text-sm text-[#9a9a9a] mb-6">This is the first account, so it becomes the <span className="brand-text font-semibold">Vozelto Owner</span> (Super Admin) with access to every workspace.</p>
            <form onSubmit={submitSetup} className="space-y-4">
              <div>
                <Label className="label-caps">Your name</Label>
                <Input data-testid="setup-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tom" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <div>
                <Label className="label-caps">Email</Label>
                <Input data-testid="setup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@business.com" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <div>
                <Label className="label-caps">Password</Label>
                <Input data-testid="setup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <div>
                <Label className="label-caps">Confirm password</Label>
                <Input data-testid="setup-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter password" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <Button data-testid="setup-submit" type="submit" disabled={busy} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <ShieldStar size={16} className="mr-1.5" /> {busy ? "Creating…" : "Create Owner Account"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="card-matte p-8" data-testid="login-card">
            <h1 className="font-head text-xl font-semibold mb-1">Sign in</h1>
            <p className="text-sm text-[#9a9a9a] mb-6">Access your marketing command center.</p>
            <form onSubmit={submitLogin} className="space-y-4">
              {loginError && (
                <div data-testid="login-error" role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                  {loginError}
                </div>
              )}
              <div>
                <Label className="label-caps">Email</Label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@business.com" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <div>
                <Label className="label-caps">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="bg-[#10101A] border-white/10 mt-1" />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <SignIn size={16} className="mr-1.5" /> {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] uppercase tracking-wider text-[#5a5a5a]">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <Button data-testid="google-login-btn" type="button" onClick={continueWithGoogle}
              className="w-full bg-white text-[#1A1A28] hover:bg-white/90 font-semibold">
              <GoogleLogo size={17} weight="bold" className="mr-2" /> Continue with Google
            </Button>
            <div className="text-center mt-4">
              <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-[#9a9a9a] hover:text-white">Forgot password?</Link>
            </div>
            <div className="text-center mt-3 pt-3 border-t border-white/8 text-sm text-[#9a9a9a]">
              New to Vozelto?{" "}
              <Link data-testid="login-signup-link" to="/signup" className="brand-text font-semibold hover:underline">Start your 14-day free trial</Link>
            </div>
          </div>
        )}

        {mode === "login" && (
          <p className="text-center text-xs text-[#5a5a5a] mt-5">Invited by your agency? Use the link in your invitation to set a password.</p>
        )}
      </div>
    </div>
  );
}
